import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Button, Field, FormError, Input } from "../components/ui";
import { AuthLayout } from "../features/auth/auth-layout";
import { authClient } from "../lib/auth-client";
import { safeRedirect } from "../lib/utils";

export const Route = createFileRoute("/two-factor")({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === "string" ? { redirect: search.redirect } : {},
  component: TwoFactor,
});

function TwoFactor() {
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"totp" | "backup">("totp");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setPending(true);
    const result =
      mode === "totp"
        ? await authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, "") })
        : await authClient.twoFactor.verifyBackupCode({ code: code.trim() });
    setPending(false);
    if (result.error) {
      setError(
        result.error.status === 401
          ? "Your sign-in timed out. Please sign in again."
          : "That code didn't work. Try again.",
      );
      return;
    }
    queryClient.clear();
    await navigate({ to: safeRedirect(redirect) });
  };

  return (
    <AuthLayout
      title="Two-factor authentication"
      description={
        mode === "totp"
          ? "Enter the 6-digit code from your authenticator app."
          : "Enter one of your backup codes."
      }
      footer={<Link to="/sign-in">Back to sign in</Link>}
    >
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
        {mode === "totp" ? (
          <Field label="Authentication code">
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
        ) : (
          <Field label="Backup code">
            <Input
              autoComplete="off"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
        )}
        <FormError message={error} />
        <Button type="submit" loading={pending} disabled={!code}>
          Verify
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setMode(mode === "totp" ? "backup" : "totp");
            setCode("");
            setError(undefined);
          }}
        >
          {mode === "totp" ? "Use a backup code instead" : "Use your authenticator app"}
        </Button>
      </form>
    </AuthLayout>
  );
}
